import { defineStore } from 'pinia';
import useChatService, { type ChatMessage } from '@/api/ChatService';
import { clearChatThread, fetchChatMessages } from '@/queries/chat';

export interface IndexedChatMessage extends ChatMessage {
    id: number;
}

/**
 * How many messages the transcript keeps on screen. Matches the server's own
 * replay limit, so a long session shows the same window a reload would.
 */
const MAX_TRANSCRIPT_MESSAGES = 100;

interface ChatState {
    messages: IndexedChatMessage[];
    streamingContent: string;
    isStreaming: boolean;
    loadingHistory: boolean;
    /** True from clicking "New chat" until the server has let the thread go. */
    clearingThread: boolean;
    /** The organisation whose history is currently loaded, or null before any load. */
    loadedFor: string | null;
    /** Identifies which conversation is on screen; see `beginTranscript`. */
    transcriptSeq: number;
    nextMessageId: number;
}

/**
 * The transcript of the assistant conversation, shared by every surface that
 * shows it.
 *
 * The floating widget and the full AI Assistant page talk to one server-side
 * thread per user and organisation, so they must not keep separate copies of
 * it — a question asked in one would leave the other showing a transcript that
 * no longer matches what the assistant remembers.
 *
 * Every slow operation here can finish after the transcript it belongs to has
 * been replaced: a history load arriving while the user is already typing, a
 * stream still running when they switch organisation, a load landing after
 * they start a new chat. Each captures the generation it began in and drops
 * itself if the transcript has moved on, so late work never writes into a
 * conversation it does not belong to.
 */
export const useChatStore = defineStore('chat', {
    state: (): ChatState => ({
        messages: [],
        streamingContent: '',
        isStreaming: false,
        loadingHistory: false,
        clearingThread: false,
        loadedFor: null,
        transcriptSeq: 0,
        nextMessageId: 0,
    }),

    actions: {
        /**
         * Declare that the transcript now shows a different conversation, and
         * return the generation standing for it. Anything already in flight
         * captured an older number and will discard itself.
         */
        beginTranscript(): number {
            this.transcriptSeq += 1;

            return this.transcriptSeq;
        },

        /** Whether work started in `seq` still belongs on screen. */
        isCurrent(seq: number): boolean {
            return seq === this.transcriptSeq;
        },

        /**
         * Forget everything about the current session.
         *
         * A conversation belongs to whoever was signed in when it happened, and
         * this store outlives a sign-out: log out and back in as a colleague in
         * the same organisation and the guard in `loadHistory` would see the
         * organisation it already has loaded and show them the previous user's
         * transcript. The generation moves on rather than resetting, so work
         * still in flight from the old session cannot match its way back in.
         */
        reset() {
            this.beginTranscript();
            this.discardStream();
            this.messages = [];
            this.loadingHistory = false;
            this.clearingThread = false;
            this.loadedFor = null;
        },

        /**
         * Load the conversation for an organisation, unless it is already the
         * one on screen. Switching organisation replaces the transcript, since
         * each organisation has a thread of its own.
         */
        async loadHistory(organisationPublicId: string) {
            if (!organisationPublicId || this.loadedFor === organisationPublicId) {
                return;
            }

            // An answer still arriving belongs to the organisation being left,
            // and the server has already recorded it there. Dropping it here
            // keeps it out of the transcript that is about to replace this one.
            this.discardStream();

            // Nothing of the outgoing organisation may stay on screen while the
            // new one loads. Sending is allowed during a load — a question
            // supersedes history still on its way — so leaving the old
            // transcript up lets that question be appended to it, and the two
            // organisations stay mixed together until the next switch.
            this.messages = [];

            // Claim the organisation up front so concurrent callers — the widget
            // and the page both mounting — do not each issue a request.
            this.loadedFor = organisationPublicId;
            this.loadingHistory = true;
            const seq = this.beginTranscript();

            const history = await fetchChatMessages(organisationPublicId).catch(startWithNoHistory);

            if (this.isCurrent(seq)) {
                this.settleHistory(history);
            }
        },

        /**
         * Show a finished history load.
         *
         * A null load failed, and releasing the claim on the organisation is
         * what lets it be tried again: leaving it set would make the guard in
         * `loadHistory` treat the organisation as already on screen, so one
         * dropped request would strand an empty transcript until the user
         * switched organisation and came back.
         *
         * @param history Messages loaded, or null when the load failed.
         */
        settleHistory(history: ChatMessage[] | null) {
            this.loadingHistory = false;
            this.messages = (history ?? []).map((message) => this.withId(message));

            if (history === null) {
                this.loadedFor = null;
            }
        },

        /**
         * Ask a question and stream the answer into the transcript.
         */
        send(organisationPublicId: string, prompt: string, helpKey?: string | null) {
            // Sending while a clear is still in flight strands the turn: the
            // request reads the thread before the delete lands, so the answer
            // is recorded into the conversation being discarded, and the
            // compare-and-set that follows matches the row the delete removed.
            // Nothing then points at it, and the reload the user trusts shows
            // an empty transcript.
            if (this.isStreaming || this.clearingThread) {
                return;
            }

            // A question is newer than any history still on its way, so it
            // takes the transcript over rather than being wiped when that load
            // lands — otherwise the user watches their own prompt vanish.
            const seq = this.beginTranscript();

            this.loadingHistory = false;
            this.pushMessage({ role: 'user', content: prompt });
            this.isStreaming = true;
            this.streamingContent = '';

            abortController = useChatService().streamChat(
                organisationPublicId,
                prompt,
                (text: string) => this.appendChunk(seq, text),
                () => this.settleStream(seq),
                (error: string) => this.failStream(seq, error),
                helpKey,
            );
        },

        /** Add streamed text, unless its conversation has left the screen. */
        appendChunk(seq: number, text: string) {
            if (this.isCurrent(seq)) {
                this.streamingContent += text;
            }
        },

        /**
         * Bank a finished answer and return the transcript to its resting
         * state. An answer whose conversation is no longer on screen is
         * dropped: the server recorded it against the conversation it was
         * asked in, which is not the one being shown.
         */
        settleStream(seq: number) {
            if (this.isCurrent(seq) && this.streamingContent) {
                this.pushMessage({ role: 'assistant', content: this.streamingContent });
            }

            this.discardStream();
        },

        /** Report a failed answer against the conversation that asked for it. */
        failStream(seq: number, error: string) {
            this.settleStream(seq);

            if (this.isCurrent(seq)) {
                this.pushMessage({ role: 'assistant', content: `Error: ${error}` });
            }
        },

        /** Abandon the answer in flight without keeping any of it. */
        discardStream() {
            abortController?.abort();
            abortController = null;
            this.streamingContent = '';
            this.isStreaming = false;
        },

        /**
         * Abandon the answer in progress, keeping whatever arrived so far.
         *
         * Aborting only stops this browser reading: the server drains the rest
         * and records the whole turn, so the next load shows the full answer.
         */
        stop() {
            if (!abortController) {
                return;
            }

            if (this.streamingContent) {
                this.pushMessage({ role: 'assistant', content: this.streamingContent });
            }

            this.discardStream();
        },

        /**
         * Start a new conversation, dropping the transcript on screen and the
         * context the assistant would otherwise carry into the next question.
         */
        async startNewChat(organisationPublicId: string) {
            // Two clears in flight together end badly: the first to return
            // reopens the composer while the second is still on its way, and
            // that late delete discards the thread the next question just
            // started, leaving its answer on screen pointing at nothing.
            if (this.clearingThread) {
                return;
            }

            // Supersede a history load still in flight, or it would land after
            // the delete and put the discarded conversation back on screen.
            const seq = this.beginTranscript();

            this.discardStream();
            this.loadingHistory = false;
            this.clearingThread = true;

            // Clear only once the server has let go of the thread. Emptying the
            // transcript first would tell the user the conversation was
            // discarded while it was still there to be resurrected by the next
            // reload, along with anything they said after clicking the button.
            const cleared = await clearChatThread(organisationPublicId).then(itWorked, itFailed);

            this.clearingThread = false;

            if (!this.isCurrent(seq)) {
                return;
            }

            if (!cleared) {
                this.pushMessage({
                    role: 'assistant',
                    content: 'Error: Could not start a new chat. Please try again.',
                });

                return;
            }

            this.messages = [];
        },

        pushMessage(message: ChatMessage) {
            this.messages.push(this.withId(message));

            if (this.messages.length > MAX_TRANSCRIPT_MESSAGES) {
                this.messages = this.messages.slice(-MAX_TRANSCRIPT_MESSAGES);
            }
        },

        withId(message: ChatMessage): IndexedChatMessage {
            this.nextMessageId += 1;

            return { ...message, id: this.nextMessageId };
        },
    },
});

/**
 * Held outside the store because an AbortController is not state to render and
 * must not be made reactive.
 */
let abortController: AbortController | null = null;

/**
 * History is a convenience: failing to load it must not stop the user asking a
 * question, so the transcript starts empty. `null` rather than `[]` so the
 * caller can tell "nothing to show yet" from "we never found out".
 */
function startWithNoHistory(): null {
    return null;
}

function itWorked(): boolean {
    return true;
}

function itFailed(): boolean {
    return false;
}
